/**
 * Handlebars templates for cards / popups / empty state.
 *
 * One isolated Handlebars environment per map config (helpers registered on it only), the
 * three templates compiled once, and every rendered string sanitised through a per-instance
 * DOMPurify before it touches the DOM. Handlebars already HTML-escapes {{values}}; the
 * sanitiser is defence in depth for what an admin wrote into the template itself.
 */

import Handlebars from 'handlebars/dist/handlebars.min.js';
import createDOMPurify from 'dompurify';
import { tr } from '../../i18n/standalone';
import type { LocationMapClientConfig, MapStringKey, NormalizedLocation } from '../../config/locationMaps';
import { MAP_STRING_KEYS } from '../../config/locationMaps';

/** Built-in copy for {{t}} — literal keys so the i18n checker can see every one of them. */
const TPL_FALLBACK: Record<MapStringKey, string> = {
  showOnMap: 'results:map.tpl.showOnMap',
  directions: 'results:map.tpl.directions',
  call: 'results:map.tpl.call',
  website: 'results:map.tpl.website',
  profile: 'results:map.tpl.profile',
  contact: 'results:map.tpl.contact',
  stocking: 'results:map.tpl.stocking',
  onDisplay: 'results:map.tpl.onDisplay',
  noResultsTitle: 'results:map.tpl.noResultsTitle',
  noResultsBody: 'results:map.tpl.noResultsBody',
};

/** What a template sees: the location plus a few derived, display-ready fields. */
export interface LocationView extends NormalizedLocation {
  hasDistance: boolean;
  distanceText: string;
  showUnits: string;
}

export interface TemplateSet {
  card: (loc: LocationView) => string;
  infowindow: (loc: LocationView) => string;
  noresults: (ctx: Record<string, unknown>) => string;
  /** Compile errors, one per broken template (the built-in fallback is used for those). */
  errors: string[];
}

export function resolveMapString(config: Pick<LocationMapClientConfig, 'strings'>, key: string): string {
  const own = config.strings?.[key];
  if (typeof own === 'string' && own.trim() !== '') return own;
  if ((MAP_STRING_KEYS as readonly string[]).includes(key)) return tr(TPL_FALLBACK[key as MapStringKey]);
  return key;
}

const FALLBACK_CARD = '<div class="lm-card" data-lm-id="{{id}}"><h5 class="lm-card__name">{{name}}</h5><p class="lm-card__line">{{city}}, {{region}}</p></div>';
const FALLBACK_INFO = '<div class="lm-popup__body"><strong>{{name}}</strong><div>{{city}}, {{region}}</div></div>';
const FALLBACK_NONE = '<div class="lm-empty"><strong>{{t "noResultsTitle"}}</strong></div>';

/**
 * @param config the map's client config (templates + strings)
 * @param opts   isolate: a fresh Handlebars env + DOMPurify per call (one per map instance)
 */
export function createTemplateSet(config: Pick<LocationMapClientConfig, 'templates' | 'strings'>): TemplateSet {
  const env = Handlebars.create();
  const purifier = createDOMPurify(window);
  purifier.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer');
  });

  env.registerHelper('t', (key: unknown) => resolveMapString(config, String(key ?? '')));
  env.registerHelper('eq', (a: unknown, b: unknown) => a === b);
  env.registerHelper('shouldShowFooter', (a: unknown, b: unknown) => !!a || !!b);
  env.registerHelper('default', (v: unknown, d: unknown) => (v === null || v === undefined || v === '' ? d : v));
  env.registerHelper('join', (list: unknown, sep: unknown) => (Array.isArray(list) ? list.join(typeof sep === 'string' ? sep : ', ') : ''));
  env.registerHelper('tel', (e164: unknown, phone: unknown) => {
    const raw = typeof e164 === 'string' && e164 !== '' ? e164 : String(phone ?? '');
    const digits = raw.replace(/[^\d+]/g, '');
    return digits === '' ? '#' : `tel:${digits}`;
  });
  env.registerHelper('directionsUrl', (loc: unknown) => {
    const l = (loc && typeof loc === 'object' ? loc : {}) as Partial<NormalizedLocation>;
    if (typeof l.lat !== 'number' || typeof l.lng !== 'number') return '#';
    const sp = new URLSearchParams({ api: '1', destination: `${l.lat},${l.lng}` });
    if (l.placeId) sp.set('destination_place_id', l.placeId);
    return `https://www.google.com/maps/dir/?${sp.toString()}`;
  });

  const errors: string[] = [];
  const compile = (name: string, src: string, fallback: string): HandlebarsTemplateDelegate => {
    try {
      const fn = env.compile(src && src.trim() !== '' ? src : fallback, { noEscape: false });
      // Force a parse now so a broken template is reported at load, not on first render.
      fn({});
      return fn;
    } catch (e) {
      errors.push(`${name}: ${(e as Error).message}`);
      return env.compile(fallback);
    }
  };
  const card = compile('card', config.templates.card, FALLBACK_CARD);
  const infowindow = compile('infowindow', config.templates.infowindow, FALLBACK_INFO);
  const noresults = compile('noresults', config.templates.noresults, FALLBACK_NONE);

  const clean = (html: string) => purifier.sanitize(html, { ADD_ATTR: ['target'] }) as string;
  return {
    card: (loc) => clean(card(loc)),
    infowindow: (loc) => clean(infowindow(loc)),
    noresults: (ctx) => clean(noresults(ctx)),
    errors,
  };
}

/** Render a template source once against a sample (admin preview). Throws on compile errors. */
export function renderTemplateOnce(src: string, view: unknown, strings: Record<string, string>): string {
  const set = createTemplateSet({ templates: { card: src, infowindow: src, noresults: src }, strings });
  if (set.errors.length) throw new Error(set.errors[0]);
  return set.card(view as LocationView);
}
